import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';
import { ItsoRegistrationService } from 'src/app/angularJS/services/itso-registration/itso-registration.service';
import { SchemeService } from 'src/app/angularJS/services/scheme/scheme.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';

/**
* A Controller used to edit a selected scheme
*/
export class SchemeEditController {
  public static $inject = [
    '$translate',
    'ACTToolbarService',
    'userService',
    'UserMessages',
    '$stateParams',
    'itsoRegistrationService',
    'schemeService',
    '$rootScope',
    'QueryBuilder',
    'LinkBuilder',
    '$q'
  ];

  public schemeName: string;
  public availableProducts: Array<InterACT.Interfaces.ItsoRegistrations.ItsoRegistration>;
  public availableProductsCount: number;
  public selectedProducts: Array<any>;
  public selectedProductsCount: number;
  public filtersForAvailableProducts: InterACT.Interfaces.IAssociatedRuleFilters;
  public filtersForSelectedProducts: InterACT.Interfaces.IAssociatedRuleFilters;
  private scheme: InterACT.Interfaces.Schemes.Scheme;
  private currentLocale: string;

  constructor(
    private $translate: ng.translate.ITranslateService,
    private ActToolbarService: ACTToolbarService,
    private userService: UserService,
    private UserMessages: UserMessagesService,
    private $stateParams: { schemeId: string },
    private itsoRegistrationService: ItsoRegistrationService,
    private schemeService: SchemeService,
    private $rootScope,
    private queryBuilder: QueryBuilder,
    private linkBuilder: LinkBuilder,
    private $q: ng.IQService
  ) {
    this.activate();
  }

  /**
   * Removes a product from the chosen scheme
   *
   * @param product The product we wish to remove from the chosen scheme
   */
  public removeProduct = (product: InterACT.Interfaces.SchemeAssociationRules.SchemeAssociationRule) => {
    this.$rootScope.showSpinner = true;
    this.schemeService.removeRuleFromScheme(product.id).then(() => {
      this.$rootScope.showSpinner = false;
      const index = this.selectedProducts.indexOf(product);
      this.selectedProducts.splice(index, 1);
      this.selectedProductsCount = this.selectedProducts.length;
      this.UserMessages.showToast('SCHEME_EDIT.SCHEME_UPDATED', { schemeName: this.schemeName });
    });
  }

  /**
   * Add a product to the chosen scheme
   *
   * @param product The product we wish to add to the chosen scheme
   */
  public addProduct = (product) => {
    const ruleToCreate: InterACT.Interfaces.SchemeAssociationRules.SchemeAssociationRule = {
      scheme: {
        abbreviation: this.scheme.reference,
        link: this.linkBuilder.getSchemesLink(this.scheme.id)
      },
      ruleType: 'JOURNEY_PRODUCT',
      mappingFields: {
        oid: product.oid,
        typ: product.typ,
        ptyp: product.ptyp
      },
      name: product.organisationName,
      description: product.description
    };

    if (product.cpicc) {
      ruleToCreate.mappingFields.cpicc = product.cpicc;
    }

    this.$rootScope.showSpinner = true;
    this.schemeService.associateProductsWithScheme(ruleToCreate).then((response) => {
      this.$rootScope.showSpinner = false;
      product.id = (<any>response).headers.location.split('/').pop();
      this.selectedProducts.push(product);
      this.selectedProductsCount = this.selectedProducts.length;
      this.UserMessages.showToast('SCHEME_EDIT.SCHEME_UPDATED', { schemeName: this.schemeName });
    });
  }

  /**
   * Used on the view to build a product field
   *
   * @param product the product to combine fields for
   * @returns the combined fields
   */
  public concatProductFields = (product) => {
    const cpicc = product.cpicc ? `-${product.cpicc}` : '';
    return `${product.oid}-${product.typ}-${product.ptyp}${cpicc}`;
  }

  /**
   * Open the manual entry dialog for custom entry of oids, typ, ptyp and an optional cpicc
   */
  public openManualEntryDialog = () => {
    this.UserMessages.showManualEntryDialog('SCHEME_EDIT.MANUAL_ENTRY', 'ADD', 'CANCEL').then((newProductToAdd) => {
      this.addProduct(newProductToAdd);
    });
  }

  /**
   * Used to update the productSelectedState on the left hand view
   *
   * @param product The product identifier to understand what product is active
   * @returns the is-selected state if product is found in selected products
   */
  public updateProductSelectedState = (product: InterACT.Interfaces.ItsoRegistrations.ItsoRegistration) => {
    const selectedProduct = _.find(this.selectedProducts, (selected) => {
      return product.oid === parseInt(selected.oid, 10) &&
        product.typ === parseInt(selected.typ, 10) &&
        product.ptyp === parseInt(selected.ptyp, 10);
    });
    return selectedProduct ? 'is-selected' : undefined;
  }

  /**
   * Instantiates the controller and calls for getting available products, setting the scheme name and setting our current locale
   */
  private activate() {
    this.currentLocale = this.$translate.use();
    this.selectedProducts = [];
    this.availableProducts = [];

    this.$rootScope.showSpinner = true;
    this.$q.all([
      this.setScheme(),
      this.setAvailableProducts(),
      this.setSelectedProducts()
    ]).then(() => {
      this.$rootScope.showSpinner = false;
    });
  }

  /**
   * Sets the scheme name that you are currently editing
   * @returns a promise from the scheme service
   */
  private setScheme() {
    return this.schemeService.getSingleScheme(this.$stateParams.schemeId).then((response: InterACT.Interfaces.Schemes.SchemeResponse) => {
      this.scheme = response.scheme;
      this.schemeName = response.scheme.name[this.currentLocale];
    });
  }

  /**
   * Gets all available products from the ITSO Registration Service
   * @returns a promise from the ITSO registration service
   */
  private setAvailableProducts() {
    return this.itsoRegistrationService.getItsoRegistrations().then((products) => {
      this.availableProducts = products.itsoRegistrations;
      this.availableProductsCount = products.itsoRegistrations.length;
    });
  }

  /**
   * Sets the selected products by checking for any existing associated rules with the current scheme
   * @returns a promise from the scheme service
   */
  private setSelectedProducts() {
    const query = this.queryBuilder.newParam().schemeIs(this.$stateParams.schemeId);
    return this.schemeService.getAssociatedRules(query.getParam()).then((productsAssociatedToThisScheme: InterACT.Interfaces.SchemeAssociationRules.SchemeAssociationRulesResponse) => {
      this.selectedProducts = productsAssociatedToThisScheme.schemeAssociationRules.map((product) => {
        return {
          id: product.schemeAssociationRule.id,
          description: product.schemeAssociationRule.description,
          organisationName: product.schemeAssociationRule.name,
          oid: product.schemeAssociationRule.mappingFields.oid,
          typ: product.schemeAssociationRule.mappingFields.typ,
          ptyp: product.schemeAssociationRule.mappingFields.ptyp,
          cpicc: product.schemeAssociationRule.mappingFields.cpicc
        };
      });
      this.selectedProductsCount = this.selectedProducts.length;
    });
  }
}